import { intakeHref } from '../lib/referral';
import React, { useState } from 'react';
import { ArrowRight, Flower2, Heart, Dumbbell, Target } from 'lucide-react';

const CONTENT = {
  women: {
    tab: 'Women + GLP-1s',
    img: '/images/women-glp1.webp?v=2',
    alt: 'Smiling woman in mauve activewear sitting in a sunlit studio',
    accent: 'Women',
    body: 'Your hormones, life stage, and unique health factors matter. Licensed medical providers consider these factors when determining whether GLP-1 treatment is appropriate and how your plan is personalized.',
    cta: "Explore Women's Care",
    features: [
      { Icon: Flower2, title: 'Care that considers your hormones', body: 'Your cycle, perimenopause, menopause, and conditions like PCOS can affect appetite and weight. Your provider takes your hormonal health into account when reviewing your plan and dosing.' },
      { Icon: Heart, title: 'Birth control, pregnancy, and your plan', body: "GLP-1 medications are not used during pregnancy. Your provider reviews your birth control and family planning before treatment and can advise on when to stop if you're planning a pregnancy." },
      { Icon: Dumbbell, title: 'Strength as part of your plan', body: 'Muscle and bone health become more important with age, especially after menopause. Your plan may include guidance on protein intake and strength training.' },
    ],
  },
  men: {
    tab: 'Men + GLP-1s',
    img: '/images/men-glp1.webp?v=3',
    alt: 'Smiling man in an olive t-shirt sitting in a sunlit room',
    accent: 'Men',
    body: 'Your hormones, lifestyle, and health goals are unique. Licensed medical providers evaluate your health history to determine whether GLP-1 treatment is appropriate and personalize your plan accordingly.',
    cta: "Explore Men's Care",
    features: [
      { Icon: Dumbbell, title: 'Dosing built for men', body: 'Individual responses to GLP-1 medications vary. Your licensed provider personalizes dosing, side-effect management, and pacing based on your health history and goals.' },
      { Icon: Heart, title: 'Support for long-term health', body: 'Ongoing medical guidance and check-ins, with your provider monitoring your progress and cardiometabolic health throughout treatment.' },
      { Icon: Target, title: 'A plan that fits your life', body: 'Your treatment plan is designed around your schedule, training routine, and lifestyle, with support from your care team along the way.' },
    ],
  },
};

const Tabs = ({ active, onChange }) => (
  <div className="inline-flex p-1 rounded-full bg-white/60 backdrop-blur-md border border-white/70 shadow-[0_8px_24px_-12px_rgba(60,40,20,0.25)]" role="tablist">
    {Object.entries(CONTENT).map(([key, c]) => (
      <button
        key={key}
        role="tab"
        aria-selected={active === key}
        data-testid={`herhis-tab-${key}`}
        onClick={() => onChange(key)}
        className={`px-5 sm:px-7 py-2.5 rounded-full text-[14px] transition-[background-color,color,box-shadow] duration-300 ${active === key ? 'bg-white text-[color:var(--alfa-ink)] font-medium shadow-sm' : 'text-[color:var(--alfa-muted)] hover:text-[color:var(--alfa-ink)]'}`}
      >
        {c.tab}
      </button>
    ))}
  </div>
);

const Feature = ({ Icon, title, body, i, on }) => (
  <div data-testid={on ? `herhis-feature-${i + 1}` : undefined} className={`flex gap-4 p-5 rounded-2xl bg-white/55 backdrop-blur-xl border border-white/70 shadow-[0_14px_36px_-22px_rgba(60,40,20,0.4)] ${i ? 'mt-3' : ''}`}>
    <span className="h-12 w-12 shrink-0 rounded-full bg-white/90 shadow-[0_6px_18px_-10px_rgba(60,40,20,0.3)] flex items-center justify-center">
      <Icon size={24} strokeWidth={1.4} className="text-[#a9772e]" />
    </span>
    <div>
      <h3 className="text-[16.5px] font-medium text-[color:var(--alfa-ink)]">{title}</h3>
      <p className="mt-1.5 text-[14px] leading-[1.55] text-[#3a352f]">{body}</p>
    </div>
  </div>
);

const Panel = ({ c, on }) => (
  <div
    aria-hidden={!on || undefined}
    className={`[grid-area:1/1] grid grid-cols-1 xl:grid-cols-[minmax(0,500px)_1fr_minmax(0,450px)] gap-10 items-center transition-opacity duration-500 ${on ? 'opacity-100' : 'opacity-0 invisible pointer-events-none'}`}
  >
    <div>
      <p className="eyebrow">Weight loss, calibrated to you</p>
      <h2 className="mt-4 text-[38px] sm:text-[48px] 2xl:text-[60px] leading-[1.04] tracking-[-0.03em] text-[color:var(--alfa-ink)]" data-testid={on ? 'herhis-headline' : undefined}>
        What&rsquo;s different<br />for <span className="text-[color:var(--alfa-royal)]">{c.accent}</span>
      </h2>
      <p className="mt-5 text-[15.5px] 2xl:text-[17px] leading-[1.6] text-[color:var(--alfa-ink)]">{c.body}</p>
      <a href={intakeHref()} target="_blank" rel="noopener noreferrer" tabIndex={on ? undefined : -1} data-testid={on ? 'herhis-cta-btn' : undefined} className="btn-primary mt-7 inline-flex items-center gap-3 rounded-md px-7 py-4 text-[15.5px]">
        {c.cta} <ArrowRight size={17} />
      </a>
    </div>
    <div className="hidden xl:block" />
    <div>
      {c.features.map((f, i) => <Feature key={f.title} {...f} i={i} on={on} />)}
    </div>
  </div>
);

export default function HerHis() {
  const [active, setActive] = useState('women');
  return (
    <section id="treatments" data-testid="herhis-section" className="relative overflow-hidden bg-[color:var(--alfa-cream)]">
      <div className="relative xl:h-[clamp(640px,42vw,780px)]">
        <div className="relative w-full aspect-[16/9] overflow-hidden bg-[color:var(--alfa-cream)] xl:absolute xl:inset-0 xl:aspect-auto">
          {Object.entries(CONTENT).map(([key, item]) => (
            <img
              key={key}
              src={item.img}
              alt={active === key ? item.alt : ''}
              aria-hidden={active !== key || undefined}
              data-testid={active === key ? 'herhis-image' : `herhis-image-${key}-hidden`}
              className={`absolute inset-0 w-full h-full object-cover object-[center_35%] [filter:brightness(0.86)] transition-opacity duration-700 ease-out ${active === key ? 'opacity-100' : 'opacity-0'}`}
            />
          ))}
        </div>

        <div className="xl:absolute xl:inset-0 max-w-[1600px] mx-auto px-5 sm:px-8 xl:px-[72px] py-10 xl:py-10 flex flex-col">
          <div className="flex justify-center -mt-[72px] xl:mt-0 relative z-10">
            <Tabs active={active} onChange={setActive} />
          </div>
          <div className="mt-8 xl:mt-0 flex-1 grid">
            {Object.entries(CONTENT).map(([key, item]) => (
              <Panel key={key} c={item} on={active === key} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
